<template>
  <div id="app">
    <!-- PC端布局 -->
    <el-container v-if="!isMobileDevice" style="min-height: 100vh">
      <el-header>
        <div class="header-content">
          <h1 class="logo" @click="goToHome">
            <img src="/logo_64x64.png" alt="根叶情" class="logo-icon">
            <span class="logo-text">根叶情 - 追根溯源</span>
          </h1>
          
          <!-- 未登录状态的导航 -->
          <div class="nav-public" v-if="!isLoggedIn">
            <el-menu :default-active="activeIndex" mode="horizontal" @select="handleSelect">
              <el-menu-item index="/home">首页</el-menu-item>
              <el-menu-item index="/map">分布地图</el-menu-item>
              <!-- <el-menu-item index="/public-shares">公开分享</el-menu-item> -->
            </el-menu>
            <div class="nav-actions">
              <ThemeSwitcher mode="pc" />
              <el-button type="primary" size="small" @click="goToLogin">登录</el-button>
              <el-button size="small" @click="goToRegister">注册</el-button>
            </div>
          </div>
          
          <!-- 已登录状态的导航 -->
          <div class="nav-logged-in" v-else>
            <el-menu :default-active="activeIndex" mode="horizontal" @select="handleSelect">
              <el-menu-item index="/home">首页</el-menu-item>
              <el-menu-item index="/map">分布地图</el-menu-item>
              <el-submenu index="/content">
                <template slot="title">内容管理</template>
                <el-menu-item index="/tree">人物关系树</el-menu-item>
                <el-menu-item index="/milestone">人物大事记</el-menu-item>
                <el-menu-item index="/records">记录评价</el-menu-item>
                <el-menu-item index="/share">分享管理</el-menu-item>
                <el-menu-item index="/ancestral-hall">祠堂管理</el-menu-item>
                <el-menu-item index="/family">
                  家族管理
                  <el-badge v-if="familyNoticeCount > 0" :value="familyNoticeCount" class="family-notice-badge" />
                </el-menu-item>
              </el-submenu>
              <el-menu-item index="/friends">
                通讯录
                <el-badge v-if="friendRequestCount > 0" :value="friendRequestCount" class="friend-request-badge" />
              </el-menu-item>
            </el-menu>
            <ThemeSwitcher mode="pc" />
            <el-dropdown @command="handleUserCommand" class="user-dropdown">
              <span class="user-info">
                <AvatarViewer 
                  :src="userAvatar" 
                  size="custom"
                  custom-size="32px"
                  :alt="userName"
                />
                <span class="username">{{ userName }}</span>
                <UserBadge v-if="user" :user="user" size="small" />
                <i class="el-icon-arrow-down"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="profile">
                  <i class="el-icon-user"></i> 个人中心
                </el-dropdown-item>
                <el-dropdown-item divided command="logout">
                  <i class="el-icon-switch-button"></i> 退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </el-header>
      <el-main>
        <!-- 统一内容区宽度：与 share-detail 一致（1200px 居中，页面自身保留 20px 内边距） -->
        <div class="main-container">
          <!-- 家族通知横幅：登录后置顶提示新的加入申请与审核结果 -->
          <div v-if="hasFamilyNotifications" class="family-notice">
            <i class="el-icon-bell family-notice-icon"></i>
            <div class="family-notice-content">
              <p v-for="(line, index) in familyNoticeLines" :key="index" class="family-notice-line">{{ line }}</p>
            </div>
            <div class="family-notice-actions">
              <el-button type="primary" size="small" @click="goFamilyNotifications">查看</el-button>
              <el-button size="small" @click="dismissFamilyNotifications">知道了</el-button>
            </div>
          </div>
          <keep-alive>
            <router-view v-if="$route.meta.pcKeepAlive" />
          </keep-alive>
          <router-view v-if="!$route.meta.pcKeepAlive" />
        </div>
      </el-main>
      <el-footer class="app-footer">
        <div class="footer-content">
          <p class="footer-title">根叶情 - 追根溯源，连接亲情</p>
          <p class="footer-links">
            <a href="#" @click.prevent="goToLogin" v-if="!isLoggedIn">登录</a>
            <span class="divider" v-if="!isLoggedIn">|</span>
            <a href="#" @click.prevent="goToRegister" v-if="!isLoggedIn">注册</a>
            <a href="#" @click.prevent="goToProfile" v-if="isLoggedIn">个人中心</a>
            <span class="divider">|</span>
            <a href="#" @click.prevent="goToHome">首页</a>
          </p>
          <p class="footer-copyright">
            <span>© 2026 根叶情 - 追根溯源</span>
            <span class="divider">|</span>
            <a href="https://beian.miit.gov.cn/" target="_blank">沪ICP备2023029045号</a>
            <span class="divider">|</span>
            <img src="/images/beian.png" class="beian-icon">
            <a href="https://beian.mps.gov.cn/#/query/webSearch?code=31011502405877" rel="noreferrer" target="_blank">沪公网安备31011502405877号</a>
          </p>
        </div>
      </el-footer>
    </el-container>
    
    <!-- 移动端布局 -->
    <div v-else class="mobile-layout">
      <!-- 移动端导航栏 -->
      <van-nav-bar title="根叶情" fixed>
        <template #right>
          <div class="mobile-nav-actions">
            <ThemeSwitcher mode="mobile" />
            <van-icon v-if="isLoggedIn" name="user-o" @click="navigateToProfile" />
          </div>
        </template>
      </van-nav-bar>
      
      <!-- 移动端内容 -->
      <div class="mobile-content">
        <!-- 家族通知条：登录后置顶提示新的加入申请与审核结果 -->
        <van-notice-bar
          v-if="hasFamilyNotifications"
          class="family-notice-mobile"
          left-icon="volume-o"
          mode="link"
          :scrollable="false"
          :wrapable="true"
          :text="familyNoticeText"
          closeable
          @click="goFamilyNotifications"
          @close="dismissFamilyNotifications"
        />
        <keep-alive>
          <router-view v-if="$route.meta.keepAlive" />
        </keep-alive>
        <router-view v-if="!$route.meta.keepAlive" />
      </div>
      
      <!-- 移动端底部导航 -->
      <van-tabbar v-if="isLoggedIn" fixed v-model="activeTab" @change="handleTabChange">
        <van-tabbar-item name="home" icon="wap-home-o">首页</van-tabbar-item>
        <van-tabbar-item name="map" icon="location-o">地图</van-tabbar-item>
        <van-tabbar-item name="content-mgmt" icon="records-o" :info="familyNoticeCount > 0 ? familyNoticeCount : null" @click="handleContentMgmtClick">内容管理</van-tabbar-item>
        <van-tabbar-item name="friends" icon="friends-o" :info="friendRequestCount > 0 ? friendRequestCount : null">好友</van-tabbar-item>
      </van-tabbar>
      
      <!-- 移动端未登录状态底部导航 -->
      <van-tabbar v-else fixed v-model="activeTab" @change="handleTabChange">
        <van-tabbar-item name="home" icon="wap-home-o">首页</van-tabbar-item>
        <van-tabbar-item name="map" icon="location-o">地图</van-tabbar-item>
        <!-- <van-tabbar-item name="public-shares" icon="eye-o">公开分享</van-tabbar-item> -->
        <van-tabbar-item name="login" icon="user-o">登录</van-tabbar-item>
      </van-tabbar>      
      
      <!-- 内容管理弹出层 -->
      <van-popup v-model="showContentMgmt" position="bottom" round>
        <div class="content-mgmt-popup">
          <div class="popup-title">请选择要管理的内容</div>
          <div class="popup-items">
            <div class="popup-item" @click="navigateToContent('tree')">
              <van-icon name="cluster-o" />
              <span>家谱/人物关系树</span>
            </div>
            <div class="popup-item" @click="navigateToContent('milestone')">
              <van-icon name="clock-o" />
              <span>大事记</span>
            </div>
            <div class="popup-item" @click="navigateToContent('records')">
              <van-icon name="records-o" />
              <span>记录</span>
            </div>
            <div class="popup-item" @click="navigateToContent('share')">
              <van-icon name="share-o" />
              <span>分享</span>
            </div>
            <div class="popup-item" @click="navigateToContent('ancestral-hall')">
              <van-icon name="wap-home-o" />
              <span>祠堂管理</span>
            </div>
            <div class="popup-item" @click="navigateToContent('family')">
              <van-icon name="cluster-o" />
              <span>家族管理</span>
            </div>
          </div>
        </div>
      </van-popup>
    </div>
  </div>
</template>

<script>
import { isMobile } from './utils/device'
import AvatarViewer from '@/components/AvatarViewer'
import ThemeSwitcher from '@/components/ThemeSwitcher'
import UserBadge from '@/components/UserBadge'

export default {
  name: 'App',
  components: {
    AvatarViewer,
    ThemeSwitcher,
    UserBadge
  },
  data() {
    return {
      isMobileDevice: false,
      activeIndex: '/home',
      activeTab: 'home',
      isLoggedIn: false,
      user: null,
      showContentMgmt: false,
      friendRequestCount: 0,
      // 家族通知：待审核申请数（管理者）与未读审核结果（申请人）
      familyPendingCount: 0,
      familyResults: [],
      // 当前已加载通知的用户，切换账号时重置
      notifiedUserId: null
    }
  },
  computed: {
    userName() {
      return this.user ? (this.user.nickname || this.user.username) : ''
    },
    userAvatar() {
      return this.user ? (this.user.avatar || '') : ''
    },
    userInitial() {
      if (!this.user) return ''
      const name = this.user.nickname || this.user.username
      return name ? name.charAt(0) : ''
    },
    // 家族通知总数：用于菜单与底部导航角标
    familyNoticeCount() {
      return this.familyPendingCount + this.familyResults.length
    },
    hasFamilyNotifications() {
      return this.isLoggedIn && this.familyNoticeCount > 0
    },
    // PC 横幅按条展示，每条一个业务事件
    familyNoticeLines() {
      const lines = []
      if (this.familyPendingCount > 0) {
        lines.push(`您管理的家族有 ${this.familyPendingCount} 条新的加入申请待审核`)
      }
      this.familyResults.forEach(app => {
        const familyName = app.family && app.family.name ? app.family.name : '家族'
        const result = app.status === 'APPROVED' ? '已通过' : '已被拒绝'
        lines.push(`您申请加入「${familyName}」${result}`)
      })
      return lines
    },
    // 移动端通知条：多条合并为一行
    familyNoticeText() {
      return this.familyNoticeLines.join('；')
    }
  },
  mounted() {
    this.isMobileDevice = isMobile()
    this.checkLoginStatus()
    this.updateActiveState()
  },
  watch: {
    $route() {
      this.updateActiveState()
      this.checkLoginStatus()
    }
  },
  methods: {
    checkLoginStatus() {
      const userStr = localStorage.getItem('user')
      this.isLoggedIn = userStr !== null
      if (this.isLoggedIn) {
        try {
          this.user = JSON.parse(userStr)
          // 同步最新积分与徽章（本地缓存可能停留在登录时的旧值）
          this.refreshUserInfo()
          this.loadFriendRequestCount()
          // 切换账号时重置家族通知状态，避免沿用上一个账号的提示
          if (this.user && this.notifiedUserId !== this.user.id) {
            this.notifiedUserId = this.user.id
            this.familyPendingCount = 0
            this.familyResults = []
          }
          this.loadFamilyNotifications()
        } catch (e) {
          this.user = null
          this.isLoggedIn = false
        }
      } else {
        this.user = null
        this.friendRequestCount = 0
        this.notifiedUserId = null
        this.familyPendingCount = 0
        this.familyResults = []
      }
    },
    // 拉取最新用户信息并回写本地缓存，保证顶部昵称旁积分徽章及时更新
    async refreshUserInfo() {
      if (!this.user || !this.user.id) return
      try {
        const response = await this.$api.user.getInfo(this.user.id)
        if (response.data && response.data.id) {
          this.user = response.data
          // 同步到全局 store，保证评论等组件取到的当前用户也带最新积分徽章
          this.$store.commit('SET_USER', response.data)
        }
      } catch (e) {
        console.error('Refresh user info error:', e)
      }
    },
    // 拉取家族通知：管理者待审申请 + 申请人未读审核结果
    async loadFamilyNotifications() {
      if (!this.user || !this.user.id) return
      try {
        const response = await this.$api.family.getNotifications(this.user.id)
        const data = response.data || {}
        this.familyPendingCount = data.pendingApplicationCount || 0
        this.familyResults = data.reviewedApplications || []
      } catch (error) {
        console.error('Load family notifications error:', error)
      }
    },
    // 点击「查看」：先同步已读再跳转，避免返回时横幅再次弹出
    async goFamilyNotifications() {
      const target = this.familyResults.length > 0 ? '/profile?tab=families' : '/family'
      await this.markFamilyNotificationsRead()
      // 忽略重复导航异常（已在目标页面时）
      this.$router.push(target).catch(() => {})
    },
    // 点击「知道了」：本地立即隐藏，并同步服务端已读状态
    dismissFamilyNotifications() {
      this.markFamilyNotificationsRead()
    },
    // 标记家族通知已读并清空本地展示
    async markFamilyNotificationsRead() {
      if (!this.hasFamilyNotifications) return
      this.familyPendingCount = 0
      this.familyResults = []
      if (this.user && this.user.id) {
        try {
          await this.$api.family.markNotificationsRead({ userId: this.user.id })
        } catch (error) {
          console.error('Mark family notifications read error:', error)
        }
      }
    },
    async loadFriendRequestCount() {
      try {
        if (!this.user || !this.user.id) return
        const response = await this.$api.friendship.getUnreadRequestCount({ userId: this.user.id })
        if (response.data && response.data.success) {
          this.friendRequestCount = response.data.count || 0
        }
      } catch (error) {
        console.error('Load friend request count error:', error)
      }
    },
    updateActiveState() {
      const path = this.$route.path
      if (this.isMobileDevice) {
        if (path.includes('/home')) {
          this.activeTab = 'home'
        } else if (path.includes('/map')) {
          this.activeTab = 'map'
        } else if (path.includes('/tree')) {
          this.activeTab = 'tree'
        } else if (path.includes('/records')) {
          this.activeTab = 'records'
        } else if (path.includes('/friends')) {
          this.activeTab = 'friends'
        } else if (path.includes('/share')) {
          this.activeTab = 'share'
        } else if (path.includes('/public-shares')) {
          this.activeTab = 'public-shares'
        } else if (path.includes('/login')) {
          this.activeTab = 'login'
        } else if (path.includes('/register')) {
          this.activeTab = 'register'
        }
      } else {
        this.activeIndex = path
      }
    },
    handleSelect(key, keyPath) {
      this.$router.push(key)
    },
    handleTabChange(tab) {
      switch (tab) {
        case 'home':
          this.$router.push('/home')
          break
        case 'map':
          this.$router.push('/map')
          break
        case 'content-mgmt':
          break
        case 'friends':
          this.$router.push('/friends')
          break
        case 'public-shares':
          this.$router.push('/public-shares')
          break
        case 'login':
          this.$router.push('/login')
          break
        case 'register':
          this.$router.push('/register')
          break
      }
    },
    handleContentMgmtClick() {
      this.showContentMgmt = !this.showContentMgmt
    },
    navigateToContent(type) {
      this.showContentMgmt = false
      switch (type) {
        case 'tree':
          this.$router.push('/tree')
          break
        case 'milestone':
          this.$router.push('/milestone')
          break
        case 'records':
          this.$router.push('/records')
          break
        case 'share':
          this.$router.push('/share')
          break
        case 'ancestral-hall':
          this.$router.push('/ancestral-hall')
          break
        case 'family':
          this.$router.push('/family')
          break
      }
    },
    navigateToProfile() {
      this.$router.push('/profile')
    },
    
    goToHome() {
      this.$router.push('/home')
    },
    
    goToLogin() {
      this.$router.push('/login')
    },
    
    goToRegister() {
      this.$router.push('/register')
    },
    
    goToPublicShares() {
      this.$router.push('/public-shares')
    },
    
    goToProfile() {
      this.$router.push('/profile')
    },
    
    handleUserCommand(command) {
      switch (command) {
        case 'profile':
          this.$router.push('/profile')
          break
        case 'logout':
          this.logout()
          break
      }
    },
    
    logout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$store.dispatch('logout')
        this.user = null
        this.isLoggedIn = false
        this.$message.success('退出登录成功')
        this.$router.push('/')
      }).catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
// 颜色统一指向全局主题变量，主色随主题切换
$primary-color: var(--accent);
$secondary-color: var(--accent-light);
$text-color: var(--ink);
$light-bg: var(--paper-2);
$dark-bg: var(--footer-bg);
$white: #FFFDF8;

// 混合宏
@mixin transition {
  transition: all 0.3s ease;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: $text-color;
}

/* PC端样式 */
.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  
  .logo {
    margin: 0;
    font-size: 24px;
    color: var(--ink);
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    @include transition;
    
    &:hover {
      opacity: 0.9;
    }
  }
}

/* 导航布局 */
.nav-public,
.nav-logged-in {
  display: flex;
  align-items: center;
  gap: 20px;
}

.nav-actions {
  display: flex;
  gap: 10px;
}

/* 用户下拉菜单 */
.user-dropdown {
  margin-left: 10px;
}

.friend-request-badge {
  margin-left: 4px;
}

/* 家族通知菜单角标 */
.family-notice-badge {
  margin-left: 4px;
  vertical-align: middle;
}

/* 家族通知横幅：主题色浅底 + 左侧强调线，登录后置顶可见 */
.family-notice {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border: 1px solid var(--accent);
  border-left: 4px solid var(--accent);
  border-radius: 6px;
  background: var(--accent-soft);
  box-shadow: 0 2px 8px rgba(var(--ink-rgb), 0.06);

  .family-notice-icon {
    font-size: 20px;
    color: var(--accent);
  }

  .family-notice-content {
    flex: 1;
  }

  .family-notice-line {
    margin: 2px 0;
    font-size: 14px;
    line-height: 1.5;
    color: var(--ink);
  }

  .family-notice-actions {
    flex-shrink: 0;
    display: flex;
    gap: 8px;
  }
}

/* 移动端家族通知条间距 */
.family-notice-mobile {
  margin-bottom: 12px;
  border-radius: 6px;
}

.user-info {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  @include transition;
  
  &:hover {
    background-color: var(--paper-deep);
  }
  
  .username {
    margin: 0 8px;
    color: var(--ink);
    font-size: 14px;
  }
  
  i {
    color: var(--ink-light);
    font-size: 12px;
  }
}

.logo-icon {
  width: 44px;
  height: 44px;
  padding: 3px;
  border-radius: 4px;
  background-color: var(--paper-card);
  border: 1px solid var(--line);
}

.logo-text {
  font-family: "Songti SC", "STSong", "SimSun", serif;
  font-size: 22px;
  letter-spacing: 2px;
  color: var(--ink);
}

.el-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  z-index: 2000;
  background-color: var(--paper-2);
  color: var(--ink);
  overflow-x: visible;
  border-bottom: 1px solid var(--line);
  box-shadow: 0 2px 10px rgba(var(--ink-rgb), 0.05);

  /* 顶部一条主色细线，呼应印章 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: var(--accent);
  }

  ::v-deep .el-menu {
    background-color: transparent;
    border-bottom: none;

    .el-menu-item {
      color: var(--ink-soft);
      font-size: 15px;
      padding: 0 16px;
      margin: 0 4px;
      height: 60px;
      line-height: 60px;
      border-radius: 4px;
      @include transition;

      &:not(.is-disabled):focus,
      &:not(.is-disabled):hover {
        background-color: var(--paper-deep);
        color: var(--accent);
      }

      &.is-active {
        background-color: transparent;
        color: var(--accent);
        font-weight: 600;

        &:hover {
          background-color: transparent;
          color: var(--accent);
        }
      }
    }

    .el-submenu {
      .el-submenu__title {
        color: var(--ink-soft);
        font-size: 15px;
        padding: 0 16px;
        margin: 0 4px;
        height: 60px;
        line-height: 60px;
        border-radius: 4px;
        @include transition;

        &:not(.is-disabled):focus,
        &:not(.is-disabled):hover {
          background-color: var(--paper-deep);
          color: var(--accent);
        }

        &:after {
          border-color: var(--ink-soft);
        }
        i {
          color: var(--ink-light);
        }
      }

      &.is-active {
        .el-submenu__title {
          background-color: transparent;
          color: var(--accent);
          font-weight: 600;
        }
      }

      /* 下拉子菜单：纸色卡片、描边、浅阴影 */
      .el-menu {
        background-color: var(--paper-card);
        border: 1px solid var(--line);
        border-radius: 4px;
        box-shadow: 0 6px 18px rgba(90, 70, 45, 0.12);
        padding: 5px 0;

        .el-menu-item {
          color: var(--ink-soft);
          padding: 0 20px;
          margin: 0;
          border-radius: 0;
          height: 40px;
          line-height: 40px;

          &:not(.is-disabled):focus,
          &:not(.is-disabled):hover {
            background-color: var(--paper-2);
            color: var(--accent);
          }

          &.is-active {
            background-color: var(--accent-soft);
            color: var(--accent);
            font-weight: 600;
          }
        }
      }
    }
  }
}

.el-main {
  /* 顶 80px = 固定菜单 60px + 20px 间距；底部 20px 与页面内边距合计 40px */
  padding: 80px 20px 20px 20px;
}

/* 统一内容区宽度：所有 PC 页面以 share-detail 的 1200px 基准居中 */
.main-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

.el-footer {
  background-color: $dark-bg;
  color: $white;
  text-align: center;
  padding: 20px 0;
  height: auto !important;
  
  &.app-footer {
    .footer-content {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
    }
    
    .footer-title {
      font-size: 16px;
      font-weight: 500;
      margin-bottom: 10px;
      color: $white;
    }
    
    .footer-links {
      margin-bottom: 10px;
      
      a {
        color: #C9BDA8;
        font-size: 14px;

        &:hover {
          color: var(--accent-light);
        }
      }
    }
    
    .footer-copyright {
      font-size: 12px;
      color: #9C9284;
      margin-bottom: 10px;
      display: flex;
      justify-content: center;
      align-items: center;
      a {
        color: #9C9284;
      }
    }
  }
}

.divider {
  color: #6E6558;
  margin: 0 10px;
}
.beian-icon {
  width: 16px;
  height: 16px;
  margin-right: 5px;
}

/* 移动端样式 */
.mobile-layout {
  min-height: 100vh;
  position: relative;
  background-color: var(--paper);
  
  .mobile-content {
    padding: 46px 16px 50px;
  }
}

/* 移动端导航栏右侧：主题切换 + 个人中心 */
.mobile-nav-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.content-mgmt-popup {
  padding: 20px;
  
  .popup-title {
    font-family: "Songti SC", "STSong", "SimSun", serif;
    font-size: 18px;
    font-weight: 600;
    text-align: center;
    margin-bottom: 20px;
    color: var(--ink);
  }
  
  .popup-items {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  
  .popup-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 16px;
    background: var(--paper-2);
    border: 1px solid var(--line-soft);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    
    &:active {
      background: var(--paper-deep);
    }
    
    .van-icon {
      font-size: 28px;
      color: var(--accent);
      margin-bottom: 8px;
    }
    
    span {
      font-size: 14px;
      color: var(--ink);
      text-align: center;
    }
  }
}
</style>
